import React from 'react';
import { formatDate } from '../../utils/format';
import { infantBaptisms, firstCommunions, baptismConfirmations, baptismList } from '../../api/endpoints';
import MergedRegisterScreen from './MergedRegisterScreen';
import type { MergedRegisterConfig } from './MergedRegisterScreen';
import { childBaptismConfig } from './ChildBaptism';
import { firstCommunionConfig } from './FirstCommunionRegister';
import { confirmationConfig } from './BaptismConfirmationRegister';
import BaptismSummary from './BaptismSummary';

// Baptism, first communion and confirmation in one register — the three stages
// of the same person, in the order a parish walks them, so the clerk moves
// between tabs instead of between modules.

const baptismConfig: MergedRegisterConfig = {
  icon: '💧',
  title: 'Baptism Register',
  pageApi: baptismList.page,
  countsApi: baptismList.counts,
  searchPlaceholder: 'Search by record, name, member ID or minister…',
  summary: BaptismSummary,
  columns: [
    { key: 'date', header: 'Date', nowrap: true, render: (r: any) => formatDate(r.date) },
    {
      key: 'person',
      header: 'Name / Member',
      render: (r: any) => (
        <>
          <div>{r.person || '-'}</div>
          {r.memberId && r.memberId !== r.person && (
            <div style={{ fontSize: '11px', opacity: 0.7 }}>{r.memberId}</div>
          )}
        </>
      )
    },
    { key: 'officiant', header: 'Minister / Bishop', render: (r: any) => r.officiant || '-' },
    { key: 'church', header: 'Church', render: (r: any) => r.church || '-' }
  ],
  types: [
    {
      key: 'BAPTISM', tabLabel: '💧 Baptism', shortLabel: 'Baptism', tone: 'accent',
      countKey: 'baptism', recordLabel: 'Baptism entry', resource: infantBaptisms,
      emptyForm: childBaptismConfig.emptyForm, toForm: childBaptismConfig.toForm,
      validate: childBaptismConfig.validate, warn: childBaptismConfig.warn,
      fields: childBaptismConfig.fields, renderDocument: childBaptismConfig.renderDocument,
      // Passed straight through: the certificate, the register
      // listing, and the mapping onto the template's placeholders.
      printModule: childBaptismConfig.printModule, printDocumentType: childBaptismConfig.printDocumentType,
      listDocumentType: childBaptismConfig.listDocumentType, printData: childBaptismConfig.printData
    },
    {
      key: 'COMMUNION', tabLabel: '🍞 Communion', shortLabel: 'Communion', tone: 'warning',
      countKey: 'communion', recordLabel: 'Communion entry', resource: firstCommunions,
      emptyForm: firstCommunionConfig.emptyForm, toForm: firstCommunionConfig.toForm,
      validate: firstCommunionConfig.validate, warn: firstCommunionConfig.warn,
      fields: firstCommunionConfig.fields, renderDocument: firstCommunionConfig.renderDocument,
      // Passed straight through: the certificate, the register
      // listing, and the mapping onto the template's placeholders.
      printModule: firstCommunionConfig.printModule, printDocumentType: firstCommunionConfig.printDocumentType,
      listDocumentType: firstCommunionConfig.listDocumentType, printData: firstCommunionConfig.printData
    },
    {
      key: 'CONFIRMATION', tabLabel: '🕊️ Confirmation', shortLabel: 'Confirmation', tone: 'success',
      countKey: 'confirmation', recordLabel: 'Confirmation entry', resource: baptismConfirmations,
      emptyForm: confirmationConfig.emptyForm, toForm: confirmationConfig.toForm,
      validate: confirmationConfig.validate, warn: confirmationConfig.warn,
      fields: confirmationConfig.fields, renderDocument: confirmationConfig.renderDocument,
      // Passed straight through: the certificate, the register
      // listing, and the mapping onto the template's placeholders.
      printModule: confirmationConfig.printModule, printDocumentType: confirmationConfig.printDocumentType,
      listDocumentType: confirmationConfig.listDocumentType, printData: confirmationConfig.printData
    }
  ]
};

const BaptismRegister = ({ isDarkMode }) => <MergedRegisterScreen config={baptismConfig} isDarkMode={isDarkMode} />;

export default BaptismRegister;
